<template>
  <StatsBar v-model:period="selectedPeriod" :loading="loading" @period-change="handlePeriodChange">
    <!-- Total submissions -->
    <StatsCard
      :label="$t('submissions.stats.total')"
      :value="stats.total"
      :trend="trends.total"
      :loading="loading"
    />

    <!-- Completed -->
    <StatsCard
      :label="$t('submissions.stats.completed')"
      :value="stats.completed"
      :trend="trends.completed"
      :loading="loading"
    />

    <!-- Partial / In Progress -->
    <StatsCard
      :label="$t('submissions.stats.partial')"
      :value="stats.partial"
      :trend="trends.partial"
      :loading="loading"
    />

    <!-- Completion Rate -->
    <StatsCard
      :label="$t('submissions.stats.completion_rate')"
      :value="completionRatePercent"
      format="percentage"
      :trend="trends.completion_rate"
      :loading="loading"
    />
  </StatsBar>
</template>

<script>
  /**
   * SubmissionStats Component
   *
   * Displays submission statistics with period selector and trend indicators.
   * Uses the reusable StatsBar and StatsCard components.
   *
   * @example
   * <SubmissionStats @stats-loaded="handleStatsLoaded" />
   */
  import { defineComponent, ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { StatsBar, StatsCard } from '@/components/partials/stats';
  import { formService } from '@/services/formService';

  export default defineComponent({
    name: 'SubmissionStats',

    components: {
      StatsBar,
      StatsCard,
    },

    props: {
      /**
       * Initial period to load
       */
      initialPeriod: {
        type: String,
        default: '30d',
      },
      /**
       * Optional form ID to filter stats
       */
      formId: {
        type: String,
        default: null,
      },
    },

    emits: ['stats-loaded', 'period-change'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance?.appContext?.config?.globalProperties?.$t || ((key) => key);

      const selectedPeriod = ref(props.initialPeriod);
      const loading = ref(true);

      // Stats data
      const stats = ref({
        total: 0,
        completed: 0,
        partial: 0,
        failed: 0,
        processing: 0,
        completion_rate: 0,
      });

      const trends = ref({
        total: null,
        completed: null,
        partial: null,
        completion_rate: null,
      });

      // Computed
      const completionRatePercent = computed(() => {
        return Math.round((stats.value.completion_rate || 0) * 100);
      });

      // Load stats from API (or calculate client-side as fallback)
      const loadStats = async (period) => {
        loading.value = true;

        try {
          // Try to fetch from API endpoint
          const response = await formService.getSubmissionStats({
            period,
            formId: props.formId,
          });

          if (response && response.current) {
            // Map API response to component stats
            const current = response.current;
            const byStatus = current.by_status || {};

            stats.value = {
              total: current.total || 0,
              completed: byStatus.COMPLETED || byStatus.received || 0,
              partial: byStatus.PARTIAL || byStatus.partial || 0,
              failed: byStatus.FAILED || 0,
              processing: byStatus.PROCESSING || byStatus.NEW || 0,
              completion_rate: calculateCompletionRate(current),
            };

            // Calculate trends from response
            if (response.previous && response.previous.total > 0) {
              const prevByStatus = response.previous.by_status || {};
              const prevCompleted = prevByStatus.COMPLETED || prevByStatus.received || 0;
              const prevPartial = prevByStatus.PARTIAL || prevByStatus.partial || 0;
              const prevCompletionRate = calculateCompletionRate(response.previous);

              trends.value = {
                total: calculateTrendPercent(stats.value.total, response.previous.total),
                completed: calculateTrendPercent(stats.value.completed, prevCompleted),
                partial: calculateTrendPercent(stats.value.partial, prevPartial),
                completion_rate: calculateTrendPercent(
                  stats.value.completion_rate * 100,
                  prevCompletionRate * 100
                ),
              };
            } else {
              trends.value = {
                total: response.trends?.total_change || null,
                completed: null,
                partial: null,
                completion_rate: null,
              };
            }
          } else {
            // Fallback: calculate client-side from submissions data
            await calculateStatsClientSide(period);
          }

          emit('stats-loaded', { period, stats: stats.value, trends: trends.value });
        } catch (error) {
          console.warn('Failed to fetch submission stats from API, calculating client-side:', error);
          // Fallback to client-side calculation
          await calculateStatsClientSide(period);
        } finally {
          loading.value = false;
        }
      };

      // Helper: Calculate completion rate
      const calculateCompletionRate = (data) => {
        const total = data.total || 0;
        const byStatus = data.by_status || {};
        const completed = byStatus.COMPLETED || byStatus.received || 0;
        return total > 0 ? completed / total : 0;
      };

      // Client-side calculation fallback
      const calculateStatsClientSide = async (period) => {
        try {
          // Fetch all submissions
          const response = await formService.getAllSubmissions({
            formId: props.formId,
            page: 1,
            limit: 1000, // Get all for calculation
          });

          const submissions = response.submissions || [];
          const now = new Date();

          // Calculate period boundaries
          const { start: periodStart, previousStart, previousEnd } = getPeriodDates(period, now);

          // Filter submissions by period
          const currentPeriodSubmissions = submissions.filter((s) => {
            const createdAt = new Date(s.created_at);
            return createdAt >= periodStart && createdAt <= now;
          });

          const previousPeriodSubmissions = previousStart
            ? submissions.filter((s) => {
                const createdAt = new Date(s.created_at);
                return createdAt >= previousStart && createdAt <= previousEnd;
              })
            : [];

          // Calculate current period stats
          const currentStats = calculatePeriodStats(currentPeriodSubmissions);
          const previousStats = calculatePeriodStats(previousPeriodSubmissions);

          stats.value = currentStats;

          // Calculate trends
          if (previousStats.total > 0) {
            trends.value = {
              total: calculateTrendPercent(currentStats.total, previousStats.total),
              completed: calculateTrendPercent(currentStats.completed, previousStats.completed),
              partial: calculateTrendPercent(currentStats.partial, previousStats.partial),
              completion_rate: calculateTrendPercent(
                currentStats.completion_rate * 100,
                previousStats.completion_rate * 100
              ),
            };
          } else {
            trends.value = {
              total: null,
              completed: null,
              partial: null,
              completion_rate: null,
            };
          }
        } catch (error) {
          console.error('Failed to calculate stats client-side:', error);
        }
      };

      // Helper: Get period date boundaries
      const getPeriodDates = (period, now) => {
        let start, previousStart, previousEnd;

        switch (period) {
          case '30d':
            start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            previousEnd = new Date(start.getTime() - 1);
            previousStart = new Date(start.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case '90d':
            start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            previousEnd = new Date(start.getTime() - 1);
            previousStart = new Date(start.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          case 'ytd':
            start = new Date(now.getFullYear(), 0, 1);
            previousEnd = new Date(now.getFullYear() - 1, 11, 31);
            previousStart = new Date(now.getFullYear() - 1, 0, 1);
            break;
          case 'all':
          default:
            start = new Date(0);
            previousStart = null;
            previousEnd = null;
            break;
        }

        return { start, previousStart, previousEnd };
      };

      // Helper: Calculate stats for a set of submissions
      const calculatePeriodStats = (submissions) => {
        const completed = submissions.filter((s) => s.is_complete || s.status === 'received');
        const partial = submissions.filter((s) => !s.is_complete || s.status === 'partial');

        const total = submissions.length;
        const completionRate = total > 0 ? completed.length / total : 0;

        return {
          total,
          completed: completed.length,
          partial: partial.length,
          failed: 0,
          processing: 0,
          completion_rate: completionRate,
        };
      };

      // Helper: Calculate trend percentage
      const calculateTrendPercent = (current, previous) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return Math.round(((current - previous) / previous) * 100);
      };

      // Handle period change
      const handlePeriodChange = (period) => {
        emit('period-change', period);
        loadStats(period);
      };

      // Initial load
      onMounted(() => {
        loadStats(selectedPeriod.value);
      });

      return {
        selectedPeriod,
        loading,
        stats,
        trends,
        completionRatePercent,
        handlePeriodChange,
      };
    },
  });
</script>

